/**
 * The one card renderer. 2D lists, the deal table's CanvasTextures and the marketing hero all
 * draw cards through `drawCardFace` / `drawCardBack`, so every surface matches exactly.
 *
 * Cards are laid out on a 250 × 350 design grid (2.5 × 3.5 poker proportions) and scaled to
 * the requested pixel size × DPR, so output is crisp at any size.
 *
 * @typedef {{ id: string, suit: string, name: string, fullName?: string, dailyGrind?: boolean,
 *   happinessTrio?: boolean, perPlayer?: boolean, playerSlot?: number | null,
 *   pairKey?: string | null }} Card  (subset of the API.md catalog item)
 */
import { drawIcon, iconFor, SUIT_ICONS } from './cardIcons.js';

export const CARD_ASPECT = 3.5 / 2.5;
const GW = 250;
const GH = 350;
const RADIUS = 15; // ~6% of width

/** Suit metadata (colors from DESIGN.md). `icon` names an entry in cardIcons.ICONS. */
export const SUITS = {
  home: { id: 'home', name: 'Home', label: 'HOME', color: '#C8553D', icon: SUIT_ICONS.home },
  out: { id: 'out', name: 'Out', label: 'OUT', color: '#2A7F8F', icon: SUIT_ICONS.out },
  caregiving: {
    id: 'caregiving',
    name: 'Caregiving',
    label: 'CAREGIVING',
    color: '#C0577A',
    icon: SUIT_ICONS.caregiving,
  },
  magic: { id: 'magic', name: 'Magic', label: 'MAGIC', color: '#6B4FA0', icon: SUIT_ICONS.magic },
  wild: { id: 'wild', name: 'Wild', label: 'WILD', color: '#C9A227', icon: SUIT_ICONS.wild },
  unicorn: {
    id: 'unicorn',
    name: 'Unicorn Space',
    label: 'UNICORN SPACE',
    color: '#6F78C9',
    icon: SUIT_ICONS.unicorn,
    gradient: ['#7F7FD5', '#86A8E7', '#91EAE4'],
  },
};
export const SUIT_ORDER = ['home', 'out', 'caregiving', 'magic', 'wild', 'unicorn'];

const AMBER = '#B7791F';
const GOLD = '#C9A227';
const THEMES = {
  light: { stock: '#FFFCF5', stock2: '#F7F0E2', ink: '#1F2433', muted: '#5B6170', hair: '#E4DCCB' },
  dark: { stock: '#2A2824', stock2: '#221F1B', ink: '#F3EDE2', muted: '#B5AC9D', hair: '#3D3830' },
  // Cream stock dimmed for dark pages (CRITIQUE #24): no glare on charcoal, still card stock.
  dim: { stock: '#EDE6D6', stock2: '#E2D9C6', ink: '#1F2433', muted: '#565B68', hair: '#D6CCB8' },
};

/** True when the page is in dark mode (explicit `<html data-theme>` wins over the OS). */
export function pageIsDark() {
  if (typeof document === 'undefined') return false;
  const t = document.documentElement.dataset.theme;
  if (t === 'dark') return true;
  if (t === 'light') return false;
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
}

const DISPLAY = "'Fraunces', 'Iowan Old Style', Georgia, serif";
const UI = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";

/* ------------------------------------------------------------------ fonts */

const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400..700;1,9..144,400..600&family=Inter:wght@400..700&display=swap';
/** @type {Promise<void> | null} */
let fontsPromise = null;

/**
 * Ensures Fraunces + Inter are loaded before drawing (canvas won't wait for webfonts).
 * Injects the Google Fonts stylesheet if the page hasn't already. Resolves after ≤ 3 s
 * regardless, so a slow font CDN never blocks the cards (they fall back to Georgia/system).
 */
export function ensureCardFonts() {
  if (fontsPromise) return fontsPromise;
  if (typeof document === 'undefined') return Promise.resolve();
  if (!document.querySelector('link[href*="family=Fraunces"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONTS_HREF;
    document.head.appendChild(link);
  }
  const load = Promise.all([
    document.fonts.load(`600 28px Fraunces`),
    document.fonts.load(`italic 500 28px Fraunces`),
    document.fonts.load(`600 12px Inter`),
    document.fonts.load(`500 12px Inter`),
  ]).then(() => undefined);
  const timeout = new Promise((r) => setTimeout(r, 3000));
  fontsPromise = Promise.race([load, timeout]).catch(() => undefined);
  return fontsPromise;
}

/* ---------------------------------------------------------------- helpers */

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x @param {number} y @param {number} w @param {number} h @param {number} r
 */
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** @param {string} hex @param {number} a */
function alpha(hex, a) {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/** Tiny deterministic PRNG so paper grain is stable per card. */
function rng(seed) {
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) | 0;
  return () => {
    s = (s * 1664525 + 1013904223) | 0;
    return ((s >>> 0) % 10000) / 10000;
  };
}

/**
 * Splits "Meals (Kids; School Lunch)" → { title: "Meals", sub: "Kids · School Lunch" }.
 * @param {Card} card
 */
export function cardTitleParts(card) {
  const name = card.name ?? card.fullName ?? '';
  const m = name.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  let title = m ? m[1] : name;
  let sub = m ? m[2].replace(/;\s*/g, ' · ') : '';
  if (card.perPlayer && card.playerSlot) sub = sub ? `${sub} · Player ${card.playerSlot}` : '';
  if (!title) title = name;
  if (title.includes(' / ')) {
    const [a, b] = title.split(' / ');
    title = a;
    sub = sub || b;
  }
  return { title, sub };
}

/**
 * Word-wraps `text` to at most `maxLines`, shrinking the font from `size` down to `minSize`.
 * @returns {{ lines: string[], size: number }}
 */
function fitText(ctx, text, font, size, minSize, maxWidth, maxLines) {
  for (let s = size; s >= minSize; s -= 1) {
    ctx.font = font(s);
    const words = text.split(/\s+/);
    const lines = [];
    let line = '';
    for (const w of words) {
      const t = line ? `${line} ${w}` : w;
      if (ctx.measureText(t).width <= maxWidth) line = t;
      else {
        if (line) lines.push(line);
        line = w;
      }
    }
    if (line) lines.push(line);
    const widest = Math.max(...lines.map((l) => ctx.measureText(l).width));
    if (lines.length <= maxLines && widest <= maxWidth) return { lines, size: s };
  }
  ctx.font = font(minSize);
  return { lines: [text], size: minSize };
}

/** Letter-spaced small caps (manual tracking; ctx.letterSpacing isn't universal). */
function trackedText(ctx, text, x, y, tracking, align = 'left') {
  const widths = [...text].map((ch) => ctx.measureText(ch).width);
  const total = widths.reduce((a, b) => a + b, 0) + tracking * (text.length - 1);
  let cx = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
  const prev = ctx.textAlign;
  ctx.textAlign = 'left';
  [...text].forEach((ch, i) => {
    ctx.fillText(ch, cx, y);
    cx += widths[i] + tracking;
  });
  ctx.textAlign = prev;
  return total;
}

/* ------------------------------------------------------------------ faces */

/**
 * Draws a card face filling the canvas area (0,0)–(width,height) in CSS px × dpr.
 * Callers size the canvas to width*dpr × height*dpr; this function sets its own transform.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Card} card
 * `index: true` draws the table layout: the title as a big top-left corner index (like a playing
 * card), so a card stays identifiable when it's overlapped in a fan or pile, and the medallion
 * moves below it. `stamp` lands an "Agreed" letterpress seal bottom-right (MSC agreed by both).
 * @param {{ width: number, height?: number, dpr?: number, theme?: 'light' | 'dark' | 'dim',
 *   index?: boolean, stamp?: { initials?: string, date?: string } | null }} opts
 */
export function drawCardFace(
  ctx,
  card,
  { width, height, dpr = 1, theme = 'light', index = false, stamp = null },
) {
  const h = height ?? width * CARD_ASPECT;
  const suit = SUITS[card.suit] ?? SUITS.home;
  const wild = card.suit === 'wild';
  const unicorn = card.suit === 'unicorn';
  const t = wild
    ? { stock: '#2E2E2E', stock2: '#232323', ink: '#F5EBD0', muted: '#BBAF8F', hair: '#4A463C' }
    : (THEMES[/** @type {keyof typeof THEMES} */ (theme)] ?? THEMES.light);
  const accent = suit.color;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.scale((width * dpr) / GW, (h * dpr) / GH);
  ctx.textBaseline = 'alphabetic';

  // Card stock with a soft top-light.
  roundRect(ctx, 0, 0, GW, GH, RADIUS);
  ctx.save();
  ctx.clip();
  const bg = ctx.createLinearGradient(0, 0, 0, GH);
  bg.addColorStop(0, t.stock);
  bg.addColorStop(1, t.stock2);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, GW, GH);

  // Paper grain (stable per card).
  const rand = rng(card.id ?? 'x');
  ctx.fillStyle = wild ? 'rgba(255,255,255,0.035)' : 'rgba(80,60,30,0.045)';
  for (let i = 0; i < 260; i++) ctx.fillRect(rand() * GW, rand() * GH, 0.7, 0.7);

  // Suit wash behind the medallion.
  const wash = ctx.createRadialGradient(125, 128, 10, 125, 128, 150);
  if (unicorn) {
    wash.addColorStop(0, alpha('#91EAE4', 0.38));
    wash.addColorStop(0.5, alpha('#86A8E7', 0.2));
    wash.addColorStop(1, alpha('#7F7FD5', 0));
  } else {
    wash.addColorStop(0, alpha(accent, wild ? 0.16 : 0.1));
    wash.addColorStop(1, alpha(accent, 0));
  }
  ctx.fillStyle = wash;
  ctx.fillRect(0, 0, GW, GH);
  if (theme === 'dim' && !wild) {
    roundRect(ctx, 0, 0, GW, GH, RADIUS);
    ctx.lineWidth = 10;
    ctx.strokeStyle = 'rgba(60,45,20,0.06)';
    ctx.stroke();
  }
  ctx.restore();

  // Inner frame: suit hairline + corner ticks.
  ctx.lineWidth = 0.9;
  ctx.strokeStyle = wild ? alpha(GOLD, 0.7) : alpha(accent, 0.38);
  roundRect(ctx, 9, 9, GW - 18, GH - 18, RADIUS - 6);
  ctx.stroke();
  if (unicorn) {
    const ir = ctx.createLinearGradient(0, 0, GW, GH);
    for (const [i, col] of suit.gradient.entries()) ir.addColorStop(i / 2, col);
    ctx.strokeStyle = ir;
    ctx.lineWidth = 2.2;
    roundRect(ctx, 9, 9, GW - 18, GH - 18, RADIUS - 6);
    ctx.stroke();
  }

  // Suit index, top-left.
  ctx.fillStyle = wild ? GOLD : accent;
  ctx.font = `700 9.5px ${UI}`;
  trackedText(ctx, suit.label, 22, 32, 1.6);
  drawIcon(ctx, suit.icon, GW - 22 - 13, 21, 13, { color: wild ? GOLD : accent, weight: 2 });

  const { title, sub } = cardTitleParts(card);
  const cx = 125;
  if (index) {
    // Corner index: big left-aligned title under the suit label.
    ctx.fillStyle = t.ink;
    ctx.textAlign = 'left';
    const fit = fitText(ctx, title, (s) => `650 ${s}px ${DISPLAY}`, 34, 17, 142, 3);
    const lh = fit.size * 1.04;
    fit.lines.forEach((l, i) => {
      ctx.fillText(l, 20, 46 + fit.size * 0.86 + i * lh);
    });
    let iy = 46 + fit.size * 0.86 + (fit.lines.length - 1) * lh;
    if (sub) {
      ctx.fillStyle = t.muted;
      const subFit = fitText(ctx, sub.toUpperCase(), (s) => `650 ${s}px ${UI}`, 10, 7.5, 200, 2);
      subFit.lines.forEach((l, i) => {
        trackedText(ctx, l, 21, iy + 17 + i * 12, 0.8);
      });
      iy += 17 + (subFit.lines.length - 1) * 12;
    }
    const my = Math.max(222, iy + 66);
    ctx.beginPath();
    ctx.arc(cx, my, 50, 0, Math.PI * 2);
    ctx.fillStyle = wild ? 'rgba(255,255,255,0.04)' : alpha(accent, unicorn ? 0.06 : 0.08);
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = wild ? alpha(GOLD, 0.55) : alpha(accent, 0.3);
    ctx.stroke();
    drawIcon(ctx, iconFor(card), cx - 29, my - 29, 58, {
      color: wild ? GOLD : unicorn ? '#5A63B8' : accent,
      weight: 1.45,
    });
  }

  // Medallion with the card icon.
  const cy = 128;
  if (!index) {
    ctx.beginPath();
    ctx.arc(cx, cy, 56, 0, Math.PI * 2);
    ctx.fillStyle = wild ? 'rgba(255,255,255,0.04)' : alpha(accent, unicorn ? 0.06 : 0.08);
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = wild ? alpha(GOLD, 0.55) : alpha(accent, 0.3);
    ctx.stroke();
    ctx.beginPath();
    ctx.setLineDash([0.1, 4.2]);
    ctx.lineCap = 'round';
    ctx.lineWidth = 1.4;
    ctx.arc(cx, cy, 63, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    drawIcon(ctx, iconFor(card), cx - 32, cy - 32, 64, {
      color: wild ? GOLD : unicorn ? '#5A63B8' : accent,
      weight: 1.35,
    });

    // Title (Fraunces), up to 3 lines, auto-fit.
    ctx.fillStyle = t.ink;
    ctx.textAlign = 'center';
    const fit = fitText(ctx, title, (s) => `600 ${s}px ${DISPLAY}`, 27, 16, 196, 3);
    const lh = fit.size * 1.1;
    const titleTop = 228 - ((fit.lines.length - 1) * lh) / 2 + (sub ? -4 : 2);
    fit.lines.forEach((l, i) => {
      ctx.fillText(l, cx, titleTop + i * lh);
    });
    let y = titleTop + (fit.lines.length - 1) * lh;
    if (sub) {
      ctx.fillStyle = t.muted;
      const subFit = fitText(ctx, sub.toUpperCase(), (s) => `600 ${s}px ${UI}`, 8.5, 6.5, 190, 2);
      subFit.lines.forEach((l, i) => {
        trackedText(ctx, l, cx, y + 19 + i * 11, 1, 'center');
      });
      y += 19 + (subFit.lines.length - 1) * 11;
    }
  }

  // Footer ornament.
  const fy = 300;
  ctx.strokeStyle = wild ? alpha(GOLD, 0.5) : alpha(accent, 0.35);
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(98, fy);
  ctx.lineTo(118, fy);
  ctx.moveTo(132, fy);
  ctx.lineTo(152, fy);
  ctx.stroke();
  ctx.save();
  ctx.translate(cx, fy);
  ctx.rotate(Math.PI / 4);
  ctx.fillStyle = wild ? GOLD : accent;
  ctx.fillRect(-2.2, -2.2, 4.4, 4.4);
  ctx.restore();

  // Markers: Daily Grind (bottom-left), Happiness Trio / player (bottom-right).
  ctx.textAlign = 'left';
  if (card.dailyGrind) {
    drawIcon(ctx, 'cup', 20, 311, 17, { color: AMBER, weight: 1.9, fill: alpha(AMBER, 0.14) });
    ctx.fillStyle = AMBER;
    ctx.font = `700 7px ${UI}`;
    trackedText(ctx, 'DAILY GRIND', 40, 325, 1);
  }
  if (card.happinessTrio) {
    const col = card.suit === 'unicorn' ? '#5A63B8' : SUITS.caregiving.color;
    drawIcon(ctx, 'heartSmile', GW - 20 - 17, 311, 17, {
      color: col,
      weight: 1.9,
      fill: alpha(col, 0.12),
    });
    ctx.fillStyle = col;
    ctx.font = `700 7px ${UI}`;
    trackedText(ctx, 'HAPPINESS TRIO', GW - 42, 325, 1, 'right');
  } else if (wild) {
    ctx.fillStyle = alpha(GOLD, 0.85);
    ctx.font = `italic 500 10px ${DISPLAY}`;
    ctx.textAlign = 'center';
    ctx.fillText('play when life happens', cx, 326);
  }
  if (stamp) drawStamp(ctx, stamp, accent);
  ctx.restore();
}

/**
 * Letterpress "Agreed" seal (signature moment: both partners signed this card's MSC).
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ initials?: string, date?: string }} stamp
 * @param {string} color
 */
function drawStamp(ctx, stamp, color) {
  ctx.save();
  ctx.translate(196, 262);
  ctx.rotate(-0.22);
  ctx.globalAlpha = 0.86;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  ctx.arc(0, 0, 31, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.arc(0, 0, 26.5, 0, Math.PI * 2);
  ctx.stroke();
  ctx.textAlign = 'center';
  ctx.font = `700 7.2px ${UI}`;
  trackedText(ctx, 'AGREED', 0, -11, 1.4, 'center');
  ctx.font = `italic 650 15px ${DISPLAY}`;
  ctx.fillText(stamp.initials ?? '✓', 0, 5);
  ctx.font = `700 6.4px ${UI}`;
  trackedText(ctx, (stamp.date ?? '').toUpperCase(), 0, 16, 0.8, 'center');
  ctx.restore();
}

/**
 * Card back: ink, a faint repeating "FP" monogram, gold hairline frame and medallion.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ width: number, height?: number, dpr?: number }} opts
 */
export function drawCardBack(ctx, { width, height, dpr = 1 }) {
  const h = height ?? width * CARD_ASPECT;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.scale((width * dpr) / GW, (h * dpr) / GH);

  roundRect(ctx, 0, 0, GW, GH, RADIUS);
  ctx.save();
  ctx.clip();
  const bg = ctx.createLinearGradient(0, 0, GW, GH);
  bg.addColorStop(0, '#262C3D');
  bg.addColorStop(1, '#171B27');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, GW, GH);

  // Monogram lattice.
  ctx.font = `italic 600 11px ${DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(201,162,39,0.13)';
  for (let row = 0; row < 22; row++) {
    for (let col = 0; col < 10; col++) {
      const x = col * 28 + (row % 2 ? 14 : 0);
      const y = row * 17 + 8;
      ctx.fillText('FP', x, y);
    }
  }
  const glow = ctx.createRadialGradient(125, 175, 10, 125, 175, 170);
  glow.addColorStop(0, 'rgba(38,44,61,0.95)');
  glow.addColorStop(0.45, 'rgba(38,44,61,0.6)');
  glow.addColorStop(1, 'rgba(23,27,39,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, GW, GH);
  ctx.restore();

  // Gold frames.
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 1;
  roundRect(ctx, 10, 10, GW - 20, GH - 20, RADIUS - 6);
  ctx.stroke();
  ctx.strokeStyle = alpha(GOLD, 0.45);
  ctx.lineWidth = 0.6;
  roundRect(ctx, 14, 14, GW - 28, GH - 28, RADIUS - 8);
  ctx.stroke();

  // Medallion.
  ctx.beginPath();
  ctx.arc(125, 175, 46, 0, Math.PI * 2);
  ctx.fillStyle = '#1F2433';
  ctx.fill();
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(125, 175, 51, 0, Math.PI * 2);
  ctx.strokeStyle = alpha(GOLD, 0.45);
  ctx.lineWidth = 0.6;
  ctx.stroke();
  ctx.fillStyle = '#E9D8A6';
  ctx.font = `italic 500 25px ${DISPLAY}`;
  ctx.fillText('Fair', 125, 172);
  ctx.fillText('Play', 125, 196);
  ctx.fillStyle = GOLD;
  ctx.font = `700 6.5px ${UI}`;
  trackedText(ctx, 'DECK', 125, 147, 2.4, 'center');
  ctx.restore();
}

/* ---------------------------------------------------------- conveniences */

/** @type {Map<string, HTMLCanvasElement>} */
const canvasCache = new Map();

/**
 * Returns a cached, drawn canvas for a card face (or the back when `card` is null).
 * @param {Card | null} card
 * @param {{ width: number, dpr?: number, theme?: 'light' | 'dark' }} opts
 */
export function cardCanvas(card, { width, dpr = 1, theme = 'light' }) {
  const key = `${card ? card.id : '__back'}|${width}|${dpr}|${theme}`;
  let cv = canvasCache.get(key);
  if (cv) return cv;
  cv = document.createElement('canvas');
  cv.width = Math.round(width * dpr);
  cv.height = Math.round(width * CARD_ASPECT * dpr);
  const ctx = /** @type {CanvasRenderingContext2D} */ (cv.getContext('2d'));
  if (card) drawCardFace(ctx, card, { width, dpr, theme });
  else drawCardBack(ctx, { width, dpr });
  if (canvasCache.size > 400) canvasCache.clear();
  canvasCache.set(key, cv);
  return cv;
}

/** Drops cached canvases (call after fonts finish loading if you drew early). */
export function clearCardCache() {
  canvasCache.clear();
}

/**
 * PNG data URL of a card face (for <img>, CSS backgrounds, OG previews).
 * @param {Card | null} card
 * @param {{ width?: number, dpr?: number, theme?: 'light' | 'dark' }} [opts]
 */
export function cardFaceDataUrl(card, { width = 250, dpr = 2, theme = 'light' } = {}) {
  return cardCanvas(card, { width, dpr, theme }).toDataURL('image/png');
}

/**
 * A ready-to-insert card element: a DPR-aware <canvas> sized `size` CSS px wide, labelled for
 * screen readers. Redraws once the webfonts are ready if they weren't yet.
 * @param {Card | null} card  null renders the back
 * `theme: 'auto'` (default) dims the card stock on dark pages (CRITIQUE #24).
 * @param {{ size?: number, theme?: 'light' | 'dark' | 'dim' | 'auto', className?: string,
 *   stamp?: { initials?: string, date?: string } | null }} [opts]
 */
export function renderCardElement(
  card,
  { size = 160, theme: themeOpt = 'auto', className = 'fp-card', stamp = null } = {},
) {
  const theme = themeOpt === 'auto' ? (pageIsDark() ? 'dim' : 'light') : themeOpt;
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const cv = document.createElement('canvas');
  cv.className = className;
  cv.width = Math.round(size * dpr);
  cv.height = Math.round(size * CARD_ASPECT * dpr);
  cv.style.width = `${size}px`;
  cv.style.height = `${size * CARD_ASPECT}px`;
  cv.style.borderRadius = `${(size * RADIUS) / GW}px`;
  cv.setAttribute('role', 'img');
  cv.setAttribute(
    'aria-label',
    card
      ? `${card.fullName ?? card.name} — ${SUITS[card.suit]?.name ?? ''} card${card.dailyGrind ? ', Daily Grind' : ''}`
      : 'Fair Play card, face down',
  );
  const ctx = /** @type {CanvasRenderingContext2D} */ (cv.getContext('2d'));
  const draw = () => {
    if (card) drawCardFace(ctx, card, { width: size, dpr, theme, stamp });
    else drawCardBack(ctx, { width: size, dpr });
  };
  draw();
  ensureCardFonts().then(draw);
  return cv;
}
